import React, { useEffect, useState, useMemo } from 'react';
import { 
    Container, 
    Paper, 
    Typography, 
    Button,
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    Box,
    Chip,
    TextField,
    InputAdornment,
    TableSortLabel,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Collapse,
    IconButton,
    Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Search, Visibility, AssignmentLate, ExpandMore, FiberManualRecord, Edit, Cancel, Assessment, AccountTree, Assignment } from '@mui/icons-material';
import { UserUtils } from '../../types';

import TitleBar from '../../components/TitleBar/TitleBar';
import EntityHierarchyGraph from '../../components/EntityHierarchyGraph/EntityHierarchyGraph';
import type { HierarchyEntity } from '../../components/EntityHierarchyGraph/EntityHierarchyGraph';
import { getUser } from '../../services/auth';
import { formatDisplayDate } from '../../utils/dateUtils';
import { 
    getShowOtherUsersReviewEntities, 
    getReviewEntityByReviewee, 
    getMyReviewEntities,
    getReviewEntityMetricThresholds,
    getRevieweeActiveQuestionnaireResponses,
    getReviewerActiveQuestionnaireResponses,
    type MetricsThreshold,
    type QuestionnaireResponse
} from '../../services/api';
import EnhancedEntityRaidDialog from './EnhancedEntityRaidDialog';
import { AddResponseDialog } from '../QuestionnaireResponses/QuestionnaireResponses';

interface ReviewEntity {
    id: number;
    name: string;
    description: string;
    org_entity_code?: string;
    review_entity_type_id: number;
    reviewers: number[];
    reviewees: number[];
    owner?: number;
    owner_name?: string;
    owner_display_name?: string;
    status?: string;
    valid_from?: string;
    valid_till?: string;
    applicable_review_freqencies: string[];
    created_by: number;
    updated_by: number;
    created_at: string;
    updated_at: string;
    review_attributes: Array<{
        id: number;
        name: string;
        description: string;
        value: string;
    }>;
    reviewer_names: string[];
    reviewee_names: string[];
    review_entity_type_name: string;
    review_entity_type?: {
        id: number;
        name: string;
        display_label: string;
        category: string;
        description?: string;
        associated_review_dimensions?: number[];
        associated_review_dimension_names?: string[];
    };
}

type SortField = 'name' | 'description' | 'review_entity_type_name' | 'org_entity_code';
type SortDirection = 'asc' | 'desc';

export interface ViewReviewEntitiesProps {}

const ViewReviewEntities: React.FC<ViewReviewEntitiesProps> = () => {
    const [entities, setEntities] = useState<ReviewEntity[]>([]);
    const [error, setError] = useState<string>('');
    const navigate = useNavigate();
    const [userId, setUserId] = useState<number | null>(null);
    const [raidDialogOpen, setRaidDialogOpen] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState<ReviewEntity | null>(null);
    const [showOtherUsersReviewEntities, setShowOtherUsersReviewEntities] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);

    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const [filterName, setFilterName] = useState<string>('');
    const [filterType, setFilterType] = useState<string>('');
    const [filterDescription, setFilterDescription] = useState<string>('');
    const [filterEntityCode, setFilterEntityCode] = useState<string>('');
    const [selectedEntityForView, setSelectedEntityForView] = useState<ReviewEntity | null>(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [metricsThresholds, setMetricsThresholds] = useState<MetricsThreshold[]>([]);
    const [metricsLoading, setMetricsLoading] = useState(false);
    const [metricsExpanded, setMetricsExpanded] = useState(true);
    const [showHierarchy, setShowHierarchy] = useState(false);
    const [selectedEntityForHierarchy, setSelectedEntityForHierarchy] = useState<ReviewEntity | null>(null);
    const [activeReviews, setActiveReviews] = useState<QuestionnaireResponse[]>([]);
    const [activeReviewsLoading, setActiveReviewsLoading] = useState(false);
    const [isReviewer, setIsReviewer] = useState(false);
    const [canCreateReview, setCanCreateReview] = useState(false);
    const [activeReviewsExpanded, setActiveReviewsExpanded] = useState(true);
    const [reviewEntitiesExpanded, setReviewEntitiesExpanded] = useState(true);
    const [addReviewDialogOpen, setAddReviewDialogOpen] = useState(false);
    const [selectedEntityForReview, setSelectedEntityForReview] = useState<ReviewEntity | null>(null);

    // Initialize user ID first, separately from other API calls
    useEffect(() => {
        const user = getUser();
        if (user?.id) {
            console.log('DEBUG: Setting userId to:', user.id);
            setUserId(user.id);
            // Check if user has reviewer role (for list APIs)
            setIsReviewer(UserUtils.isReviewer(user));
            setCanCreateReview(UserUtils.canCreateReview(user));
        } else {
            setError('User ID not found');
        }
    }, []);

    // Fetch active reviews based on user role
    useEffect(() => {
        if (!userId) return;

        const fetchActiveReviews = async () => {
            setActiveReviewsLoading(true);
            try {
                const response = isReviewer 
                    ? await getReviewerActiveQuestionnaireResponses()
                    : await getRevieweeActiveQuestionnaireResponses();
                setActiveReviews(response.data || []);
            } catch (err) {
                console.error('Error fetching active reviews:', err);
                setActiveReviews([]);
            } finally {
                setActiveReviewsLoading(false);
            }
        };

        fetchActiveReviews();
    }, [userId, isReviewer]);

    const fetchEntities = async () => {
        setLoading(true);
        try {
            const entityResponse = await getMyReviewEntities();
            setEntities(entityResponse.data as unknown as ReviewEntity[]);
            setError('');
        } catch (err) {
            console.error('Error fetching entities:', err);
            setError('Failed to load review entities');
        } finally {
            setLoading(false);
        }
    };

    // First useEffect to fetch the config setting
    useEffect(() => {
        // Don't try to fetch entities until we have a userId
        if (!userId) return;
        fetchEntities();
    }, [userId]); // Depends only on userId

    const refreshActiveReviews = async () => {
        setActiveReviewsLoading(true);
        try {
            const response = isReviewer 
                ? await getReviewerActiveQuestionnaireResponses()
                : await getRevieweeActiveQuestionnaireResponses();
            setActiveReviews(response.data || []);
        } catch (err) {
            console.error('Error refreshing active reviews:', err);
        } finally {
            setActiveReviewsLoading(false);
        }
    };

    const handleStartReview = (entity: ReviewEntity) => {
        if (!canCreateReview) {
            return;
        }
        setSelectedEntityForReview(entity);
        setAddReviewDialogOpen(true);
    };

    const handleAddReviewSuccess = () => {
        // Refresh active reviews when a new review is created
        refreshActiveReviews();
    };

    const handleViewDetails = async (entity: ReviewEntity) => {
        setSelectedEntityForView(entity);
        setViewDialogOpen(true);
        
        // Fetch metrics thresholds for this entity
        try {
            setMetricsLoading(true);
            const metricsResponse = await getReviewEntityMetricThresholds(entity.id);
            setMetricsThresholds(metricsResponse.data);
        } catch (err) {
            console.error('Error fetching metrics thresholds:', err);
            setMetricsThresholds([]);
        } finally {
            setMetricsLoading(false);
        }
    };

    const handleCloseViewDialog = () => {
        setViewDialogOpen(false);
        setSelectedEntityForView(null);
        setMetricsThresholds([]);
        setMetricsLoading(false);
    };

    const handleOpenRaidDialog = (entity: ReviewEntity) => {
        setSelectedEntity(entity);
        setRaidDialogOpen(true);
    };

    const handleCloseRaidDialog = () => {
        setRaidDialogOpen(false);
        setSelectedEntity(null);
    };

    const handleViewHierarchy = (entity: ReviewEntity) => {
        setSelectedEntityForHierarchy(entity);
        setShowHierarchy(true);
    };

    const handleCloseHierarchy = () => {
        setShowHierarchy(false);
        setSelectedEntityForHierarchy(null);
    };

    const handleRequestSort = (field: SortField) => {
        const isAsc = sortField === field && sortDirection === 'asc';
        setSortDirection(isAsc ? 'desc' : 'asc');
        setSortField(field);
    };

    const getStatusIcon = (status: string | undefined) => {
        const statusValue = status || 'ACTIVE';
        switch (statusValue) {
            case 'ACTIVE':
                return <FiberManualRecord sx={{ color: 'success.main', fontSize: 16 }} />;
            case 'DRAFT':
                return <Edit sx={{ color: 'warning.main', fontSize: 20 }} />;
            case 'IN-ACTIVE':
                return <Cancel sx={{ color: 'error.main', fontSize: 20 }} />;
            default:
                return <FiberManualRecord sx={{ color: 'success.main', fontSize: 16 }} />;
        }
    };

    const renderReviewStatus = (review: QuestionnaireResponse) => {
        const overdueText = review.is_submission_overdue
            ? `Submission overdue${review.days_past_submission_due != null ? ` by ${review.days_past_submission_due}d` : ''}`
            : review.is_closure_overdue
                ? `Closure overdue${review.days_past_closure_due != null ? ` by ${review.days_past_closure_due}d` : ''}`
                : '';
        const overdueDueDate = review.is_submission_overdue
            ? review.submission_due_by
            : review.is_closure_overdue
                ? review.closure_due_by
                : null;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Chip
                    label={review.review_status}
                    size="small"
                    color={
                        review.review_status === 'DRAFT' ? 'default' :
                        review.review_status === 'IN_PROGRESS' ? 'info' :
                        review.review_status === 'SUBMITTED' ? 'success' :
                        review.review_status === 'CORRECTION_PENDING' ? 'warning' :
                        'default'
                    }
                />
                {review.is_overdue && (
                    <Chip
                        label={overdueText}
                        color="error"
                        size="small"
                        variant="outlined"
                    />
                )}
                {review.is_overdue && overdueDueDate && (
                    <Typography variant="caption" color="error">
                        Due by {formatDisplayDate(overdueDueDate)}
                    </Typography>
                )}
            </Box>
        );
    };

    const filteredAndSortedEntities = useMemo(() => {
        const filteredEntities = entities.filter(entity => {
            const nameMatch = filterName === '' || 
                entity.name.toLowerCase().includes(filterName.toLowerCase());
            const descriptionMatch = filterDescription === '' || 
                entity.description.toLowerCase().includes(filterDescription.toLowerCase());
            const typeMatch = filterType === '' || 
                entity.review_entity_type_name.toLowerCase().includes(filterType.toLowerCase());
            const entityCodeMatch = filterEntityCode === '' || 
                (entity.org_entity_code && entity.org_entity_code.toLowerCase().includes(filterEntityCode.toLowerCase()));
            return nameMatch && descriptionMatch && typeMatch && entityCodeMatch;
        });

        return [...filteredEntities].sort((a, b) => {
            // Handle potentially undefined values safely
            const aValue = a[sortField] as string | undefined;
            const bValue = b[sortField] as string | undefined;
            
            // If either value is undefined, handle that case
            if (aValue === undefined && bValue === undefined) return 0;
            if (aValue === undefined) return sortDirection === 'asc' ? -1 : 1;
            if (bValue === undefined) return sortDirection === 'asc' ? 1 : -1;
            
            // Compare actual values for normal cases
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [entities, filterName, filterDescription, filterType, filterEntityCode, sortField, sortDirection]);

    return (
        <>
            <TitleBar />
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                {/* Back Button Below TitleBar */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate(-1)}
                    >
                        Back
                    </Button>
                </Box>

                {/* My Active Reviews Section */}
                <Paper elevation={3} sx={{ p: 3, mt: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h5">
                            My Active Reviews
                        </Typography>
                        <IconButton
                            onClick={() => setActiveReviewsExpanded(!activeReviewsExpanded)}
                            size="small"
                        >
                            <ExpandMore
                                sx={{
                                    transform: activeReviewsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                    transition: 'transform 0.3s'
                                }}
                            />
                        </IconButton>
                    </Box>
                    
                    <Collapse in={activeReviewsExpanded}>
                    {activeReviewsLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!activeReviewsLoading && activeReviews.length === 0 && (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                            No active reviews found.
                        </Typography>
                    )}

                    {!activeReviewsLoading && activeReviews.length > 0 && (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Review Name</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Entity</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Entity Type</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Review Date</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Status</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Reviewer</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Reviewee</Typography></TableCell>
                                        <TableCell align="center"><Typography sx={{ fontWeight: 'bold' }}>Actions</Typography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activeReviews.map((review) => (
                                        <TableRow key={review.id}>
                                            <TableCell>{review.review_name}</TableCell>
                                            <TableCell>{review.review_entity_name}</TableCell>
                                            <TableCell>
                                                {typeof review.review_entity_type_name === 'string' 
                                                    ? review.review_entity_type_name 
                                                    : review.review_entity_type_name?.display_label || 'N/A'}
                                            </TableCell>
                                            <TableCell>{review.review_date}</TableCell>
                                            <TableCell>
                                                {renderReviewStatus(review)}
                                            </TableCell>
                                            <TableCell>{review.reviewer_name}</TableCell>
                                            <TableCell>{review.reviewee_name}</TableCell>
                                            <TableCell align="center">
                                                <Tooltip title={isReviewer || review.review_status === 'SUBMITTED' ? 'View Answers' : 'View/Update Answers'}>
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => navigate(`/review-answers/${review.id}/${review.review_entity_type_id || (typeof review.review_entity_type_name === 'object' ? review.review_entity_type_name?.id : '')}`)}
                                                        size="small"
                                                    >
                                                        <Assignment />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    </Collapse>
                </Paper>

                <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
                    {/* Title and filter section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h5">
                            Review Entities
                        </Typography>
                        <IconButton
                            onClick={() => setReviewEntitiesExpanded(!reviewEntitiesExpanded)}
                            size="small"
                        >
                            <ExpandMore
                                sx={{
                                    transform: reviewEntitiesExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                    transition: 'transform 0.3s'
                                }}
                            />
                        </IconButton>
                    </Box>

                    <Collapse in={reviewEntitiesExpanded}>
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Assessment color="success" />
                            <strong>Tip:</strong> Click the green <strong>Responses</strong> icon to start a new review for any entity.
                        </Typography>
                    </Box>

                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    
                    {error && (
                        <Typography color="error" sx={{ my: 2 }}>
                            {error}
                        </Typography>
                    )}
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TextField
                                            variant="outlined"
                                            placeholder="Filter by name"
                                            value={filterName}
                                            onChange={(e) => setFilterName(e.target.value)}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            variant="outlined"
                                            placeholder="Filter by description"
                                            value={filterDescription}
                                            onChange={(e) => setFilterDescription(e.target.value)}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            variant="outlined"
                                            placeholder="Filter by type"
                                            value={filterType}
                                            onChange={(e) => setFilterType(e.target.value)}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            variant="outlined"
                                            placeholder="Filter by entity code"
                                            value={filterEntityCode}
                                            onChange={(e) => setFilterEntityCode(e.target.value)}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center"></TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'name'}
                                            direction={sortField === 'name' ? sortDirection : 'asc'}
                                            onClick={() => handleRequestSort('name')}
                                        >
                                            <Typography sx={{ fontWeight: 'bold' }}>Name</Typography>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'description'}
                                            direction={sortField === 'description' ? sortDirection : 'asc'}
                                            onClick={() => handleRequestSort('description')}
                                        >
                                            <Typography sx={{ fontWeight: 'bold' }}>Description</Typography>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'review_entity_type_name'}
                                            direction={sortField === 'review_entity_type_name' ? sortDirection : 'asc'}
                                            onClick={() => handleRequestSort('review_entity_type_name')}
                                        >
                                            <Typography sx={{ fontWeight: 'bold' }}>Type</Typography>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortField === 'org_entity_code'}
                                            direction={sortField === 'org_entity_code' ? sortDirection : 'asc'}
                                            onClick={() => handleRequestSort('org_entity_code')}
                                        >
                                            <Typography sx={{ fontWeight: 'bold' }}>Org Entity Code</Typography>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="center"><Typography sx={{ fontWeight: 'bold' }}>Actions</Typography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAndSortedEntities.map((entity) => (
                                    <TableRow key={entity.id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Tooltip title={entity.status || 'ACTIVE'}>
                                                    {getStatusIcon(entity.status)}
                                                </Tooltip>
                                                {entity.name}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{entity.description}</TableCell>
                                        <TableCell>{entity.review_entity_type_name}</TableCell>
                                        <TableCell>{entity.org_entity_code}</TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                                <Tooltip title="View Details">
                                                    <IconButton size="small" onClick={() => handleViewDetails(entity)}>
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="View Hierarchy">
                                                    <IconButton size="small" onClick={() => handleViewHierarchy(entity)} color="primary">
                                                        <AccountTree />
                                                    </IconButton>
                                                </Tooltip>
                                                {canCreateReview && (
                                                <Tooltip title="Start New Review - Click to create a new review for this entity">
                                                    <IconButton size="small" onClick={() => handleStartReview(entity)} color="success">
                                                        <Assessment />
                                                    </IconButton>
                                                </Tooltip>
                                                )}
                                                <Tooltip title="RAID">
                                                    <IconButton size="small" onClick={() => handleOpenRaidDialog(entity)} color="secondary">
                                                        <AssignmentLate />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    </Collapse>
                </Paper>
            </Container>

            {/* View Entity Details Dialog */}
            <Dialog 
                open={viewDialogOpen} 
                onClose={handleCloseViewDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6">Review Entity Details</Typography>
                </DialogTitle>
                <DialogContent>
                    {selectedEntityForView && (
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>Basic Information</Typography>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Name</Typography>
                                            <Typography variant="body1">{selectedEntityForView.name}</Typography>
                                        </Box>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Description</Typography>
                                            <Typography variant="body1">{selectedEntityForView.description}</Typography>
                                        </Box>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Type</Typography>
                                            <Typography variant="body1">{selectedEntityForView.review_entity_type_name}</Typography>
                                        </Box>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Entity Code</Typography>
                                            <Typography variant="body1">{selectedEntityForView.org_entity_code || 'Not Available'}</Typography>
                                        </Box>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Status</Typography>
                                            <Chip 
                                                label={selectedEntityForView.status || 'ACTIVE'} 
                                                size="small" 
                                                color={selectedEntityForView.status === 'ACTIVE' ? 'success' : selectedEntityForView.status === 'DRAFT' ? 'warning' : 'default'}
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>People & Dates</Typography>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Owner</Typography>
                                            <Typography variant="body1">{selectedEntityForView.owner_display_name || selectedEntityForView.owner_name || 'Not set'}</Typography>
                                        </Box>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Reviewers</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                {selectedEntityForView.reviewer_names?.map((name) => (
                                                    <Chip key={name} label={name} size="small" variant="outlined" />
                                                ))}
                                            </Box>
                                        </Box>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Valid From</Typography>
                                            <Typography variant="body1">{selectedEntityForView.valid_from ? formatDisplayDate(selectedEntityForView.valid_from) : 'Not set'}</Typography>
                                        </Box>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Valid Till</Typography>
                                            <Typography variant="body1">{selectedEntityForView.valid_till ? formatDisplayDate(selectedEntityForView.valid_till) : 'Not set'}</Typography>
                                        </Box>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Created</Typography>
                                            <Typography variant="body1">{formatDisplayDate(selectedEntityForView.created_at)}</Typography>
                                        </Box>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Updated</Typography>
                                            <Typography variant="body1">{formatDisplayDate(selectedEntityForView.updated_at)}</Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            {selectedEntityForView.review_attributes && selectedEntityForView.review_attributes.length > 0 && (
                                <Grid item xs={12}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>Attributes</Typography>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell><strong>Name</strong></TableCell>
                                                            <TableCell><strong>Description</strong></TableCell>
                                                            <TableCell><strong>Value</strong></TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {selectedEntityForView.review_attributes.map((attr) => (
                                                            <TableRow key={attr.id}>
                                                                <TableCell>{attr.name}</TableCell>
                                                                <TableCell>{attr.description}</TableCell>
                                                                <TableCell>{attr.value}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                            
                            {/* Metrics Thresholds Section */}
                            <Grid item xs={12}>
                                <Card sx={{ bgcolor: 'grey.50' }}>
                                    <CardHeader 
                                        title="Project Metrics Thresholds"
                                        action={
                                            <IconButton
                                                onClick={() => setMetricsExpanded(!metricsExpanded)}
                                                aria-expanded={metricsExpanded}
                                                aria-label="show more"
                                                sx={{
                                                    transform: metricsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.3s'
                                                }}
                                            >
                                                <ExpandMore />
                                            </IconButton>
                                        }
                                    />
                                    <Divider />
                                    <Collapse in={metricsExpanded} timeout="auto" unmountOnExit>
                                        {metricsLoading ? (
                                            <CardContent>
                                                <Typography>Loading metrics thresholds...</Typography>
                                            </CardContent>
                                        ) : metricsThresholds.length > 0 ? (
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell><strong>Threshold Name</strong></TableCell>
                                                            <TableCell><strong>Dimension</strong></TableCell>
                                                            <TableCell><strong>Org Lower Bound</strong></TableCell>
                                                            <TableCell><strong>Org Upper Bound</strong></TableCell>
                                                            <TableCell><strong>Org Critical</strong></TableCell>
                                                            <TableCell><strong>Org Dashboard</strong></TableCell>
                                                            <TableCell><strong>Org Target</strong></TableCell>
                                                            <TableCell><strong>Overridable</strong></TableCell>
                                                            <TableCell><strong>Override Lower</strong></TableCell>
                                                            <TableCell><strong>Override Upper</strong></TableCell>
                                                            <TableCell><strong>Critical</strong></TableCell>
                                                            <TableCell><strong>Dashboard</strong></TableCell>
                                                            <TableCell><strong>Target</strong></TableCell>
                                                            <TableCell><strong>Override Reason</strong></TableCell>
                                                            <TableCell><strong>Is Overridden</strong></TableCell>
                                                            <TableCell><strong>Created By</strong></TableCell>
                                                            <TableCell><strong>Created At</strong></TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {metricsThresholds.map((threshold) => (
                                                            <TableRow key={threshold.org_threshold_id}>
                                                                <TableCell>{threshold.threshold_name}</TableCell>
                                                                <TableCell>{threshold.threshold_dimension}</TableCell>
                                                                <TableCell>{threshold.org_threshold_lower_bound}</TableCell>
                                                                <TableCell>{threshold.org_threshold_upper_bound}</TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={(threshold.org_is_critical ?? threshold.is_critical) ? 'Yes' : 'No'}
                                                                        color={(threshold.org_is_critical ?? threshold.is_critical) ? 'error' : 'default'}
                                                                        size="small"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={(threshold.org_is_dashboard_metric ?? threshold.is_dashboard_metric ?? true) ? 'Yes' : 'No'}
                                                                        color={(threshold.org_is_dashboard_metric ?? threshold.is_dashboard_metric ?? true) ? 'primary' : 'default'}
                                                                        size="small"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{threshold.org_target_value ?? '—'}</TableCell>
                                                                <TableCell>
                                                                    <Chip 
                                                                        label={threshold.overridable ? 'Yes' : 'No'} 
                                                                        color={threshold.overridable ? 'success' : 'default'}
                                                                        size="small"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    {threshold.is_overridden ? threshold.overriden_lower_bound : '-'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {threshold.is_overridden ? threshold.overriden_upper_bound : '-'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={threshold.is_critical ? 'Yes' : 'No'}
                                                                        color={threshold.is_critical ? 'error' : 'default'}
                                                                        size="small"
                                                                        variant={threshold.is_critical_overridden ? 'filled' : 'outlined'}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={(threshold.is_dashboard_metric ?? true) ? 'Yes' : 'No'}
                                                                        color={(threshold.is_dashboard_metric ?? true) ? 'primary' : 'default'}
                                                                        size="small"
                                                                        variant={threshold.is_dashboard_metric_overridden ? 'filled' : 'outlined'}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{threshold.target_value ?? '—'}</TableCell>
                                                                <TableCell>
                                                                    {threshold.is_overridden ? threshold.override_reason : '-'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip 
                                                                        label={threshold.is_overridden ? 'Yes' : 'No'} 
                                                                        color={threshold.is_overridden ? 'warning' : 'default'}
                                                                        size="small"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{threshold.created_by_name || threshold.created_by}</TableCell>
                                                                <TableCell>{threshold.created_at}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (
                                            <CardContent>
                                                <Typography color="textSecondary">
                                                    No metrics thresholds available for this review entity.
                                                </Typography>
                                            </CardContent>
                                        )}
                                    </Collapse>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseViewDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* RAID Dialog */}
            {selectedEntity && (
                <EnhancedEntityRaidDialog
                    open={raidDialogOpen}
                    onClose={handleCloseRaidDialog}
                    entityId={selectedEntity.id}
                    entityName={selectedEntity.name}
                    reviewEntityType={selectedEntity.review_entity_type}
                />
            )}

            {/* Entity Hierarchy Graph */}
            {showHierarchy && selectedEntityForHierarchy && (
                <EntityHierarchyGraph
                    entityId={selectedEntityForHierarchy.id}
                    allEntities={entities as unknown as HierarchyEntity[]}
                    onClose={handleCloseHierarchy}
                    onSaved={fetchEntities}
                />
            )}

            {/* Add Review Dialog */}
            {selectedEntityForReview && (
                <AddResponseDialog
                    open={addReviewDialogOpen}
                    onClose={() => setAddReviewDialogOpen(false)}
                    onSuccess={handleAddReviewSuccess}
                    reviewEntityId={selectedEntityForReview.id}
                    reviewEntityName={selectedEntityForReview.name}
                    entityReviewerIds={selectedEntityForReview.reviewers}
                    entityRevieweeIds={selectedEntityForReview.reviewees}
                />
            )}
        </>
    );
};

export { ViewReviewEntities };
